import { api } from '../DefaultService';
import type { IReportProps } from './types';

const ENDPOINT = 'v1/report';

const buildIncludeImagesQuery = (includeImages?: boolean): string => {
  if (typeof includeImages !== 'boolean') {
    return '';
  }

  return `?includeImages=${includeImages}`;
};

const requestReportBlob = async (
  path: string,
  fallbackMessage: string,
): Promise<Blob> => {
  return await api
    .get(path, {
      responseType: 'blob',
      timeout: 0,
    })
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(error?.response?.data?.message || fallbackMessage);
    });
};

export const generateStudentReport = async (alunoId: number): Promise<Blob> => {
  return await api
    .get(`${ENDPOINT}/student/${alunoId}/`, {
      responseType: 'blob',
      timeout: 0,
    })
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.message || 'Erro ao gerar o relatório do aluno!',
      );
    });
};

export const generateFacesReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/faces/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de faces!',
  );
};
export const generateEventsNoImageReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/eventsnoimage/${data.StarTime}/${data.EndTime}/${data.ExportType}`,
    'Erro ao gerar o relatório de eventos sem imagem!',
  );
};
export const generateStudentEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de cadastro de alunos!',
  );
};

export const generateStudentEvents = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/events/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de eventos de alunos!',
  );
};

export const generateStudentExitEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/exit/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de saídas de alunos!',
  );
};

export const generateStudentsWithoutEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/noevent/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de alunos sem eventos!',
  );
};

export const generateEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/events/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de eventos!',
  );
};

export const generateGeneralEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/general/events/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de eventos de funcionários!',
  );
};

export const generateResponsibleEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/responsible/events/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de eventos de responsáveis!',
  );
};

export const generateResponsibleExitEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/responsible/exit/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de saídas de responsáveis!',
  );
};

export const generateRecognitionExitEventsReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/recognition/exit/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de saídas por reconhecimento!',
  );
};
export const generateDeviceReport = async (
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/device/${data.StarTime}/${data.EndTime}/${data.ExportType}`,
    'Erro ao gerar o relatório de dispositivos!',
  );
};

export const generateStudantEventsReport = async (
  id: number,
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/${id}/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de eventos do aluno!',
  );
};

export const generateStudentEventsByStudentReport = async (
  id: number,
  data: IReportProps,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/${id}/${data.StarTime}/${data.EndTime}/${data.ExportType}${buildIncludeImagesQuery(data.IncludeImages)}`,
    'Erro ao gerar o relatório de eventos do aluno!',
  );
};

export const generateStudentEntriesLast30DaysReport = async (
  id: number,
  exportType: string,
  includeImages?: boolean,
): Promise<Blob> => {
  return await requestReportBlob(
    `${ENDPOINT}/student/last30/${id}/${exportType}${buildIncludeImagesQuery(includeImages)}`,
    'Erro ao gerar o relatório de entradas dos últimos 30 dias!',
  );
};

export const generateStudentsByClassReport = async (
  schoolClassIds: number[],
  exportType: string,
  includeImages?: boolean,
): Promise<Blob> => {
  if (!schoolClassIds.length) {
    throw new Error('Selecione ao menos uma turma para gerar o relatório.');
  }

  const query = schoolClassIds
    .map((id) => `schoolClassIds=${id}`)
    .join('&');

  const includeImagesQuery =
    typeof includeImages === 'boolean' ? `&includeImages=${includeImages}` : '';

  return await requestReportBlob(
    `${ENDPOINT}/student/class/${exportType}?${query}${includeImagesQuery}`,
    'Erro ao gerar o relatório de alunos por turma!',
  );
};
